import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory23() {
  const { lesson, course, accent } = findLesson("music-theory", "23");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT23-Relative-Modes-Animated.mp4"
        caption="The 7 relative modes — same notes, the HOME badge slides"
        hint="≈ 40 sec · watch first"
      />
      <Chapter number="23" eyebrow="Music Theory · Chapter Twenty-three" title="Same notes — new ringleader">
        <Prose>
          <p>
            We're returning to scales now, with a new question: how are scales{" "}
            <em>related</em> to each other? There are two main answers, and this chapter
            handles the first one: <strong>relative scales</strong>. Two scales are relatives
            of each other when they share the <em>same set of notes</em> but use a{" "}
            <em>different root</em>. Same alphabet — different chairperson.
          </p>
          <p>
            The clearest example is the relationship between a major scale and its{" "}
            <strong>relative minor</strong>. C major and A minor contain the same seven
            notes — every white key on the piano. But the song that ends on C feels happy
            and the song that ends on A feels sad. Same ingredients. Different ringleader.
            Different mood entirely.
          </p>
          <MTQuote>
            A relative scale will have the same set of seven notes while changing the root.
          </MTQuote>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The seven modes of the major scale" title="One scale · seven roots · seven moods">
        <Prose>
          <p>
            Take any major scale and play its seven notes — but start from a different
            note each time. Each starting note gives the scale a completely new character.
            These seven re-startings are called the <strong>modes</strong>. They carry
            ancient Greek names, borrowed by medieval church musicians who applied them
            rather differently from the Greeks — the labels stuck even though the meaning
            shifted:
          </p>
        </Prose>

        <Steps>
          <Step><strong>Ionian</strong> — start on the 1. This is the major scale itself. Bright, stable.</Step>
          <Step><strong>Dorian</strong> — start on the 2. Minor with a hopeful sixth. <em>Scarborough Fair</em>; <em>So What</em>.</Step>
          <Step><strong>Phrygian</strong> — start on the 3. Minor with a flat second. Spanish, flamenco, exotic.</Step>
          <Step><strong>Lydian</strong> — start on the 4. Major with a sharp fourth. Dreamy, floating, cinematic.</Step>
          <Step><strong>Mixolydian</strong> — start on the 5. Major with a flat seventh. Bluesy, country, rock.</Step>
          <Step><strong>Aeolian</strong> — start on the 6. The natural minor scale. Sad, classic, melancholic.</Step>
          <Step><strong>Locrian</strong> — start on the 7. Minor with a flat second AND flat fifth. Unstable, almost unusable.</Step>
        </Steps>

        <Prose>
          <p>
            All seven modes here use the <em>same seven notes</em> as the parent C major
            scale. They differ only in where you call "home". And that single difference
            transforms each scale's emotional centre — because home becomes the place all
            tension resolves to, and the intervals leading to "home" change with every new
            root.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="From C major" title="Seven scales · all white keys">
        <Prose>
          <p>
            Here's the magic. Every mode below uses only the white keys of the piano. They
            differ only in which white key you start and end on. If you can play C major,
            you already physically know how to play all seven modes — you just have to
            change the starting note.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>Start on <strong>C</strong> → C Ionian (= C major)</li>
            <li>Start on <strong>D</strong> → D Dorian</li>
            <li>Start on <strong>E</strong> → E Phrygian</li>
            <li>Start on <strong>F</strong> → F Lydian</li>
            <li>Start on <strong>G</strong> → G Mixolydian</li>
            <li>Start on <strong>A</strong> → A Aeolian (= A natural minor)</li>
            <li>Start on <strong>B</strong> → B Locrian</li>
          </ul>
          <p>
            All of these are <strong>relatives of C major</strong>. Same notes. Different
            roots. The explorer below opens on C, which shows you each mode's
            <em>formula</em>. To hear the relative modes themselves, click the matching
            tonic for each row — <strong>D</strong> for Dorian, <strong>E</strong> for
            Phrygian, <strong>F</strong> for Lydian, <strong>G</strong> for Mixolydian,
            <strong>B</strong> for Locrian. Every one of them will come out on white keys
            only, because every one of them is C major wearing a different hat.
          </p>
        </Prose>

        <ScaleExplorer tonic="C" scales={["dorian", "phrygian", "lydian", "mixolydian", "locrian"]} />
      </Chapter>

      <Chapter eyebrow="Relative minor" title="The most-used relative relationship in pop music">
        <Prose>
          <p>
            The single most important relative relationship — the one you'll meet on every
            chart you ever play — is between the major and its <strong>relative minor</strong>.
            The relative minor of any major scale is found by going up to the{" "}
            <strong>6th degree</strong>. From G major, the 6th is E — so E minor is G major's
            relative minor. From C major it's A minor. From F major, D minor.
          </p>
          <p>
            Why does it sound so different? Three intervals shift. The major 3rd (B in G
            major) becomes a minor 3rd (G is now the ♭3 of E). The major 6th (E in G major)
            becomes a minor 6th. And the major 7th (F♯) becomes a flat 7 (D, when measured
            from E). Three minor intervals replace three major intervals — and the whole
            scale tips from "happy" to "melancholic".
          </p>
        </Prose>

        <TheoryBox label="Finding the relative minor">
          <div className="text-[14px] leading-relaxed">
            Take any major key. Go up to its <strong>6th degree</strong>. That's the root of
            the relative minor.
            <br />
            G major (1=G) → 6 = E → <strong>E minor</strong>
            <br />
            C major (1=C) → 6 = A → <strong>A minor</strong>
            <br />
            F major (1=F) → 6 = D → <strong>D minor</strong>
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Relative thinking in songwriting" title="One progression · two emotional centres">
        <Prose>
          <p>
            Composers exploit relative scales constantly. Imagine you're writing a song in
            G major using the chord progression{" "}
            <span className="font-mono">G — C — D — G</span>. The whole thing feels uplifting
            because G is the centre and everything resolves home to G.
          </p>
          <p>
            Now reorder the same chords as <span className="font-mono">Em — C — D — Em</span>.
            Same key, same notes — but you've started on the relative minor. Suddenly the
            song is about leaving a sad place rather than arriving in a happy one. The lyric
            "fly far away from here" hits completely differently. Major says "I'm flying to
            something better." Minor says "I'm escaping somewhere dark."
          </p>
          <p>
            That's the magic of relative scales: they let a songwriter shift the emotional
            centre of a song <em>without changing the notes at all</em>. Just by reordering
            chords, by emphasising a different note in the melody, by ending on the relative
            tonic instead of the parent — you walk into a parallel emotional universe built
            from the same vocabulary.
          </p>
        </Prose>

        <MTQuote>
          You're just nominating a different CEO, who's going to have a different vision for
          the company, so to speak.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Beyond minor — every mode is a relative" title="Dorian, Phrygian, Lydian, Mixolydian as relatives">
        <Prose>
          <p>
            We tend to focus on the relative minor because it's the most useful pivot. But
            every mode is a relative of the major scale.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>G major's <strong>relative Dorian</strong> is A Dorian (start on the 2nd).</li>
            <li>G major's <strong>relative Phrygian</strong> is B Phrygian (start on the 3rd).</li>
            <li>G major's <strong>relative Lydian</strong> is C Lydian (start on the 4th).</li>
            <li>G major's <strong>relative Mixolydian</strong> is D Mixolydian (start on the 5th).</li>
            <li>G major's <strong>relative minor</strong> is E minor (start on the 6th).</li>
            <li>G major's <strong>relative Locrian</strong> is F♯ Locrian (start on the 7th).</li>
          </ul>
          <p>
            Each of those scales contains exactly the same notes as G major. The mode is just
            a way of saying "play the parent scale, but treat <em>this</em> note as home".
            This is why jazz players think about modes as <em>perspectives</em> on a single
            major scale rather than as completely separate scales to memorise.
          </p>
        </Prose>

        <Glossary>
          <Term name="Relative scale" def="A scale that shares the same notes as another but uses a different root. Same notes, new home." />
          <Term name="Mode" def="A scale derived by starting on a particular degree of the parent scale. Each of the seven major-scale degrees gives one of the seven classical modes." />
          <Term name="Parent scale" def="The scale a mode is borrowed from. The C major scale is the parent of all seven 'white-key' modes." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="What's next" title="Same root · different notes">
        <Prose>
          <p>
            Relative scales hold the notes constant and change the root. The next chapter
            does the opposite: it holds the <em>root</em> constant and changes the notes.
            That's the <strong>parallel</strong> approach — and it's the one composers
            reach for when they want to <em>borrow</em> from another mode without leaving
            their key centre.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/23a-modes-of-C-major.png"
          caption="The 7 modes of C major — Ionian (C-C, the major scale itself), Dorian (D-D), Phrygian (E-E), Lydian (F-F), Mixolydian (G-G), Aeolian (A-A, the natural minor), Locrian (B-B). All seven modes use the SAME seven notes; only the starting note (and therefore the formula relative to that starting note) changes."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-24"
        label="Chapter 24 · Understanding Modes - Parallel (Part 2)"
        why="Relative kept the notes, changed the root. Parallel keeps the root, changes the notes. Two ways to see the same modes — both essential."
      />
    </LessonShell>
  );
}
